<template>
  <div class="registration-dashboard" v-loading="loading">
    <div class="dashboard-header">
      <div>
        <p>{{ currentDateLabel }} · 北京时间</p>
      </div>
      <div class="date-tools">
        <el-button-group class="quick-buttons">
          <el-button
            v-for="item in quickDates"
            :key="item.key"
            :type="date === item.value ? 'primary' : 'default'"
            size="small"
            @click="selectDate(item.value)"
          >{{ item.label }}</el-button>
        </el-button-group>
        <el-date-picker
          v-model="date"
          class="date-picker"
          type="date"
          size="small"
          value-format="yyyy-MM-dd"
          placeholder="选择日期"
          :clearable="false"
          @change="fetchDashboard"
        ></el-date-picker>
        <el-button
          type="primary"
          icon="el-icon-refresh"
          size="small"
          :loading="loading"
          @click="fetchDashboard"
        >刷新</el-button>
      </div>
    </div>

    <div class="metric-grid">
      <div class="metric-card metric-registrations">
        <div class="metric-icon">
          <i class="el-icon-s-order"></i>
        </div>
        <div>
          <div class="metric-label">报名总人数</div>
          <div class="metric-value">{{ dashboard.totalRegistrationCount }}</div>
          <div class="metric-subtitle">已支付 + 免费报名</div>
        </div>
      </div>
      <div class="metric-card metric-year-on-year">
        <div class="metric-icon">
          <i class="el-icon-refresh"></i>
        </div>
        <div>
          <div class="metric-label">报名人数同比</div>
          <div class="metric-value rate-value" :class="getRateClass(dashboard.yearOverYearRate)">
            <i :class="getRateIcon(dashboard.yearOverYearRate)"></i>
            {{ formatRate(dashboard.yearOverYearRate) }}
          </div>
          <div class="metric-subtitle">较 {{ formatDateLabel(dashboard.previousYearDate) }}</div>
        </div>
      </div>
      <div class="metric-card metric-day-on-day">
        <div class="metric-icon">
          <i class="el-icon-sort"></i>
        </div>
        <div>
          <div class="metric-label">报名人数环比</div>
          <div class="metric-value rate-value" :class="getRateClass(dashboard.dayOverDayRate)">
            <i :class="getRateIcon(dashboard.dayOverDayRate)"></i>
            {{ formatRate(dashboard.dayOverDayRate) }}
          </div>
          <div class="metric-subtitle">较 {{ formatDateLabel(dashboard.previousDate) }}</div>
        </div>
      </div>
      <div class="metric-card metric-payment">
        <div class="metric-icon">
          <i class="el-icon-money"></i>
        </div>
        <div>
          <div class="metric-label">收款总额</div>
          <div class="metric-value">{{ formatMoney(dashboard.totalPaymentAmount) }}</div>
          <div class="metric-subtitle">当天支付成功订单</div>
        </div>
      </div>
      <div class="metric-card metric-sharing">
        <div class="metric-icon">
          <i class="el-icon-pie-chart"></i>
        </div>
        <div>
          <div class="metric-label">分账总额</div>
          <div class="metric-value">{{ formatMoney(dashboard.totalProfitSharingAmount) }}</div>
          <div class="metric-subtitle">主订单 + 兼项订单成功分润</div>
        </div>
      </div>
    </div>

    <section class="chart-panel hourly-panel">
      <div class="panel-title">
        <div>
          <h3>分时段报名人数</h3>
          <span>展示当天每小时的报名人数波动</span>
        </div>
        <el-tag size="small" effect="plain">单位：人</el-tag>
      </div>
      <div ref="hourlyChart" class="hourly-chart"></div>
    </section>

    <div v-if="showBrandModules" class="dashboard-content">
      <section class="chart-panel">
        <div class="panel-title">
          <div>
            <h3>赛事品牌报名统计</h3>
            <span>统计 {{ brandCount }} 个重点赛事品牌</span>
          </div>
          <el-tag size="small" effect="plain">单位：人</el-tag>
        </div>
        <div ref="brandChart" class="brand-chart"></div>
      </section>

      <section class="rank-panel">
        <div class="panel-title compact">
          <div>
            <h3>品牌排行</h3>
            <span>精确读数</span>
          </div>
        </div>
        <div class="rank-list">
          <div
            v-for="(item, index) in sortedBrandStats"
            :key="item.brand"
            class="rank-item"
          >
            <div class="rank-meta">
              <span class="rank-index">{{ index + 1 }}</span>
              <span class="rank-logo">
                <img v-if="item.logo" :src="item.logo" :alt="item.brand">
                <span v-else>{{ item.brand.slice(0, 1) }}</span>
              </span>
              <span class="rank-name">{{ item.brand }}</span>
            </div>
            <div class="rank-bar">
              <div
                class="rank-bar-inner"
                :style="{ width: getRankWidth(item.count) }"
              ></div>
            </div>
            <span class="rank-count">{{ item.count }}</span>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import ThemeManager from '@/utils/theme.js';
import { getCurrentUser } from '@/utils/auth.js';
import { REGISTRATION_DASHBOARD_BRANDS } from '@/views/components/constant.js';

const emptyDashboard = () => ({
  date: '',
  timezone: 'Asia/Shanghai',
  startAt: '',
  endAt: '',
  totalRegistrationCount: 0,
  previousDate: '',
  previousDayRegistrationCount: 0,
  previousYearDate: '',
  previousYearDayRegistrationCount: 0,
  dayOverDayRate: null,
  yearOverYearRate: null,
  totalPaymentAmount: 0,
  totalProfitSharingAmount: 0,
  hourlyStats: Array.from({ length: 24 }, (_, hour) => ({
    hour,
    label: `${String(hour).padStart(2, '0')}:00`,
    count: 0
  })),
  brandStats: REGISTRATION_DASHBOARD_BRANDS.map(item => ({
    id: item.id,
    brand: item.brand,
    keyword: item.keyword,
    logo: item.logo,
    count: 0
  }))
});

export default {
  name: 'RegistrationDailyDashboard',
  data() {
    return {
      loading: false,
      date: this.$dayjs().format('YYYY-MM-DD'),
      dashboard: emptyDashboard(),
      brandChart: null,
      hourlyChart: null,
      unsubscribeTheme: null,
      resizeTimer: null,
      currentUser: getCurrentUser()
    };
  },
  computed: {
    quickDates() {
      return [
        {
          key: 'today',
          label: '今天',
          value: this.$dayjs().format('YYYY-MM-DD')
        },
        {
          key: 'yesterday',
          label: '昨天',
          value: this.$dayjs().subtract(1, 'day').format('YYYY-MM-DD')
        },
        {
          key: 'beforeYesterday',
          label: '前天',
          value: this.$dayjs().subtract(2, 'day').format('YYYY-MM-DD')
        }
      ];
    },
    currentDateLabel() {
      return this.date ? this.$dayjs(this.date).format('YYYY年MM月DD日') : '--';
    },
    sortedBrandStats() {
      return [...this.visibleBrandStats].sort((a, b) => b.count - a.count);
    },
    maxBrandCount() {
      return Math.max(...this.sortedBrandStats.map(item => Number(item.count || 0)), 0);
    },
    brandCount() {
      return this.visibleBrandStats.length;
    },
    userBrandKeywords() {
      const keywords = this.currentUser && Array.isArray(this.currentUser.brandKeywords)
        ? this.currentUser.brandKeywords
        : [];
      return keywords.map(keyword => String(keyword).trim()).filter(Boolean);
    },
    visibleBrandStats() {
      const stats = this.dashboard.brandStats || [];
      if (!this.userBrandKeywords.length) return stats;
      return stats.filter(item => this.userBrandKeywords.some(keyword => this.isBrandKeywordMatched(keyword, item)));
    },
    showBrandModules() {
      return this.visibleBrandStats.length > 1;
    }
  },
  mounted() {
    this.fetchDashboard();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('bde-auth-updated', this.handleAuthUpdated);
    this.unsubscribeTheme = ThemeManager.subscribe(() => {
      this.renderChart();
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('bde-auth-updated', this.handleAuthUpdated);
    if (this.unsubscribeTheme) {
      this.unsubscribeTheme();
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    if (this.brandChart) {
      this.brandChart.dispose();
      this.brandChart = null;
    }
    if (this.hourlyChart) {
      this.hourlyChart.dispose();
      this.hourlyChart = null;
    }
  },
  methods: {
    handleAuthUpdated() {
      this.currentUser = getCurrentUser();
      this.$nextTick(() => {
        this.renderChart();
      });
    },
    isBrandKeywordMatched(keyword, brand) {
      const normalizedKeyword = String(keyword || '').trim().toLowerCase();
      if (!normalizedKeyword || !brand) return false;
      return [brand.brand, brand.keyword, brand.id]
        .map(value => String(value || '').trim().toLowerCase())
        .filter(Boolean)
        .some(value => value.includes(normalizedKeyword) || normalizedKeyword.includes(value));
    },
    selectDate(value) {
      if (this.date === value) {
        this.fetchDashboard();
        return;
      }
      this.date = value;
      this.fetchDashboard();
    },
    fetchDashboard() {
      if (!this.date) return;
      this.loading = true;
      this.$axios
        .get('/form-result/daily_dashboard', {
          params: {
            date: this.date
          },
          headers: {
            'x-auth-token': localStorage.getItem('bde_token')
          }
        })
        .then((res) => {
          this.dashboard = {
            ...emptyDashboard(),
            ...(res.data.res || {})
          };
          this.$nextTick(() => {
            this.renderChart();
          });
        })
        .catch((err) => {
          this.$message.error(err.response?.data?.message || '报名数据大盘加载失败');
          if (err.response?.data?.code === 'token_timeout') {
            this.$router.push('/login');
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatMoney(value) {
      return `¥${(Number(value || 0) / 100).toFixed(2)}`;
    },
    formatDateLabel(dateText) {
      return dateText ? this.$dayjs(dateText).format('YYYY年MM月DD日') : '--';
    },
    formatRate(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
      const rate = Number(value);
      return `${rate > 0 ? '+' : ''}${rate.toFixed(2)}%`;
    },
    getRateClass(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return 'rate-unavailable';
      if (Number(value) > 0) return 'rate-positive';
      if (Number(value) < 0) return 'rate-negative';
      return 'rate-neutral';
    },
    getRateIcon(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return 'el-icon-minus';
      if (Number(value) > 0) return 'el-icon-top';
      if (Number(value) < 0) return 'el-icon-bottom';
      return 'el-icon-right';
    },
    getRankWidth(count) {
      if (!this.maxBrandCount) return '0%';
      return `${Math.max(4, Math.round((Number(count || 0) / this.maxBrandCount) * 100))}%`;
    },
    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        if (this.brandChart) {
          this.brandChart.resize();
        }
        if (this.hourlyChart) {
          this.hourlyChart.resize();
        }
      }, 120);
    },
    getThemeColor(name, fallback) {
      return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
    },
    renderChart() {
      const isDark = ThemeManager.isDark();
      const axisColor = isDark ? '#8b949e' : '#909399';
      const textColor = isDark ? '#e6edf3' : '#303133';
      const splitColor = isDark ? '#30363d' : '#ebeef5';
      const brandColor = this.getThemeColor('--brand-primary', '#c04851');
      const brandRgb = this.getThemeColor('--brand-primary-rgb', '192, 72, 81');

      this.renderBrandChart({ axisColor, textColor, splitColor });
      this.renderHourlyChart({ axisColor, textColor, splitColor, brandColor, brandRgb });
    },
    renderBrandChart({ axisColor, textColor, splitColor }) {
      if (!this.showBrandModules) {
        if (this.brandChart) {
          this.brandChart.dispose();
          this.brandChart = null;
        }
        return;
      }
      if (!this.$refs.brandChart) return;
      if (!this.brandChart) {
        this.brandChart = echarts.init(this.$refs.brandChart);
      }

      const stats = this.visibleBrandStats;
      const brands = stats.map(item => item.brand);
      const counts = stats.map(item => Number(item.count || 0));

      this.brandChart.setOption({
        color: ['#c04851'],
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: (params) => {
            const item = params && params[0];
            if (!item) return '';
            return `${item.name}<br/>报名人数：${item.value}`;
          }
        },
        grid: {
          left: 16,
          right: 24,
          top: 24,
          bottom: 16,
          containLabel: true
        },
        xAxis: {
          type: 'value',
          minInterval: 1,
          axisLine: {
            lineStyle: {
              color: axisColor
            }
          },
          axisLabel: {
            color: axisColor
          },
          splitLine: {
            lineStyle: {
              color: splitColor
            }
          }
        },
        yAxis: {
          type: 'category',
          data: brands,
          inverse: true,
          axisTick: {
            show: false
          },
          axisLine: {
            lineStyle: {
              color: axisColor
            }
          },
          axisLabel: {
            color: textColor
          }
        },
        series: [
          {
            name: '报名人数',
            type: 'bar',
            data: counts,
            barMaxWidth: 22,
            itemStyle: {
              borderRadius: [0, 6, 6, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#c04851' },
                { offset: 1, color: '#e6a23c' }
              ])
            },
            label: {
              show: true,
              position: 'right',
              color: textColor
            }
          }
        ]
      }, true);

      this.brandChart.resize();
    },
    renderHourlyChart({ axisColor, textColor, splitColor, brandColor, brandRgb }) {
      if (!this.$refs.hourlyChart) return;
      if (!this.hourlyChart) {
        this.hourlyChart = echarts.init(this.$refs.hourlyChart);
      }

      const isMobile = window.innerWidth <= 768;
      const stats = this.dashboard.hourlyStats || [];
      const labels = stats.map(item => item.label);
      const counts = stats.map(item => Number(item.count || 0));

      this.hourlyChart.setOption({
        color: [brandColor],
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line'
          },
          formatter: (params) => {
            const item = params && params[0];
            if (!item) return '';
            return `${item.name}<br/>报名人数：${item.value}`;
          }
        },
        grid: {
          left: isMobile ? 6 : 16,
          right: isMobile ? 12 : 24,
          top: 26,
          bottom: isMobile ? 10 : 18,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: labels,
          axisLine: {
            lineStyle: {
              color: axisColor
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: axisColor,
            hideOverlap: true,
            interval: isMobile ? 3 : 1,
            margin: isMobile ? 10 : 8,
            formatter: (value) => {
              return isMobile ? value.replace(':00', '') : value;
            }
          }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLine: {
            lineStyle: {
              color: axisColor
            }
          },
          axisLabel: {
            color: axisColor
          },
          splitLine: {
            lineStyle: {
              color: splitColor
            }
          }
        },
        series: [
          {
            name: '报名人数',
            type: 'line',
            data: counts,
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              width: 3,
              color: brandColor
            },
            itemStyle: {
              color: brandColor,
              borderColor: textColor,
              borderWidth: 1
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `rgba(${brandRgb}, 0.28)` },
                { offset: 1, color: `rgba(${brandRgb}, 0.02)` }
              ])
            }
          }
        ]
      }, true);

      this.hourlyChart.resize();
    }
  }
};
</script>

<style scoped>
.registration-dashboard {
  color: var(--text-primary);
}

.dashboard-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.panel-title h3 {
  margin: 0;
  color: var(--text-primary);
  font-weight: 700;
}

.dashboard-header p,
.panel-title span,
.metric-subtitle {
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 13px;
}

.dashboard-header p {
  margin-top: 0;
}

.date-tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.date-picker {
  width: 150px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.hourly-panel {
  margin-bottom: 16px;
}

.metric-card,
.chart-panel,
.rank-panel {
  border: 1px solid var(--border-lighter);
  background: var(--bg-card);
  box-shadow: var(--shadow-light);
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 118px;
  box-sizing: border-box;
  padding: 20px;
  border-radius: 8px;
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  color: #fff;
  font-size: 24px;
}

.metric-registrations .metric-icon {
  background: #c04851;
}

.metric-payment .metric-icon {
  background: #2f80ed;
}

.metric-sharing .metric-icon {
  background: #16a085;
}

.metric-year-on-year .metric-icon {
  background: #7957c8;
}

.metric-day-on-day .metric-icon {
  background: #0f8f78;
}

.metric-label {
  color: var(--text-regular);
  font-size: 14px;
}

.metric-value {
  margin-top: 6px;
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 700;
  line-height: 1.15;
}

.rate-value {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 25px;
  white-space: nowrap;
}

.rate-value i {
  font-size: 18px;
  font-weight: 700;
}

.rate-positive {
  color: var(--color-success);
}

.rate-negative {
  color: var(--color-danger);
}

.rate-neutral,
.rate-unavailable {
  color: var(--text-secondary);
}

.dashboard-content {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(280px, 0.9fr);
  gap: 16px;
}

.chart-panel,
.rank-panel {
  box-sizing: border-box;
  min-width: 0;
  padding: 18px;
  border-radius: 8px;
}

.panel-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.panel-title.compact {
  margin-bottom: 12px;
}

.panel-title h3 {
  font-size: 17px;
}

.brand-chart,
.hourly-chart {
  width: 100%;
}

.brand-chart {
  height: 360px;
}

.hourly-chart {
  height: 320px;
}

.rank-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rank-item {
  display: grid;
  grid-template-columns: minmax(128px, 0.95fr) minmax(0, 1fr) 34px;
  align-items: center;
  gap: 10px;
}

.rank-meta {
  display: flex;
  align-items: center;
  min-width: 0;
}

.rank-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  margin-right: 8px;
  border-radius: 6px;
  background: var(--brand-primary-lighter);
  color: var(--brand-primary);
  font-size: 12px;
  font-weight: 700;
}

.rank-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  margin-right: 8px;
  overflow: hidden;
  border: 1px solid var(--border-lighter);
  border-radius: 6px;
  background: #fff;
  color: var(--brand-primary);
  font-size: 12px;
  font-weight: 700;
}

.rank-logo img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.rank-name {
  overflow: hidden;
  color: var(--text-regular);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-bar {
  height: 8px;
  overflow: hidden;
  border-radius: 8px;
  background: var(--border-extra-light);
}

.rank-bar-inner {
  height: 100%;
  border-radius: 8px;
  background: linear-gradient(90deg, #c04851 0%, #e6a23c 100%);
  transition: width 0.25s ease;
}

.rank-count {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 700;
  text-align: right;
}

@media (max-width: 1024px) {
  .dashboard-header {
    flex-direction: column;
  }

  .date-tools {
    justify-content: flex-start;
    width: 100%;
  }

  .dashboard-content {
    grid-template-columns: 1fr;
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .registration-dashboard {
    margin: 0;
  }

  .date-tools {
    gap: 8px;
  }

  .quick-buttons {
    display: flex;
    width: 100%;
  }

  .quick-buttons .el-button {
    flex: 1 1 0;
    min-width: 0;
    width: auto;
    padding-right: 0;
    padding-left: 0;
  }

  .date-picker,
  .date-tools > .el-button {
    width: 100%;
  }

  .dashboard-header,
  .metric-grid,
  .hourly-panel,
  .dashboard-content {
    margin-right: 0;
    margin-left: 0;
  }

  .metric-card {
    min-height: 104px;
    padding: 16px;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }

  .metric-value {
    font-size: 24px;
  }

  .brand-chart,
  .hourly-chart {
    height: 320px;
  }

  .chart-panel,
  .rank-panel {
    padding: 14px;
  }

  .rank-item {
    grid-template-columns: minmax(120px, 0.95fr) minmax(0, 1fr) 30px;
  }
}
</style>
